import { nanoid } from 'nanoid';
import * as React from 'react';

import type { W_Account_FindAllForReportTypeView_VM_User } from 'ab-api/web/account/findAllForReportTypeView/viewModel';
import type { W_ReportType_FindAllReportBlockFieldsForFilter_VM } from 'ab-api/web/reportType/findAllReportBlockFieldsForFilter';

import { formatDate } from '@acceligentllc/shared/utils/time';

import { TimeFormat } from '@acceligentllc/shared/enums/timeFormat';
import type { TimePeriodRecurrence } from '@acceligentllc/shared/enums/timePeriodRecurrence';

import { useLazyLoadWithInvalidation } from 'af-utils/react.util';

import { UNIQUE_ID_SIZE } from 'ab-common/constants/value';

import { Button, SearchInput } from '@acceligentllc/storybook';

import CustomModal from 'af-components/CustomModal';
import AddFilter from 'af-components/Table/Filters/AddFilter';

import AdditionalFilters from '../AdditionalFilters';
import { useReportTypeViewAdditionalFields } from '../AdditionalFilters/customHooks';
import ReportTypeViewTableDateFilter from '../DateFilter';
import type { JobFilterItem } from '../JobFilterItem';
import ReportTypeFilter from '../ReportTypeFilter';
import type { ReportTypeFilterItem } from '../ReportTypeFilter/types';
import type { ReportTypeViewTableAdditionalFilter } from '../types';
import { ADDITIONAL_OPTIONS } from '../types';
import styles from './styles.module.scss';

type Props = {
	closeModal: () => void;
	showModal: boolean;
	lazyLoadReportTypes: (loaded: boolean) => Promise<void>;
	reportTypeOptions: ReportTypeFilterItem[];
	currentReportTypeFilter: Nullable<ReportTypeFilterItem>;
	currentStartDate: Date;
	currentEndDate: Date;
	currentPeriod: TimePeriodRecurrence;
	currentAdditionalFilters: ReportTypeViewTableAdditionalFilter[];
	currentSearch: string;
	accountOptions: W_Account_FindAllForReportTypeView_VM_User[];
	equipmentOptions: {
		id: number;
		code: string;
		specification: Nullable<string>;
	}[];
	jobOptions: JobFilterItem[];
	lazyLoadAccountOptions: (isLoaded: boolean) => Promise<void>;
	lazyLoadEquipmentOptions: (isLoaded: boolean) => Promise<void>;
	lazyLoadJobOptions: (isLoaded: boolean) => Promise<void>;
	loadReportBlockFieldsForFilter: (reportTypeId: number, startDate: string, endDate: string) => Promise<W_ReportType_FindAllReportBlockFieldsForFilter_VM>;
	bulkEditTableFilters: (
		_reportType: ReportTypeFilterItem,
		_startDate: Date,
		_endDate: Date,
		_period: TimePeriodRecurrence,
		_search: string,
		_additionalFilters: ReportTypeViewTableAdditionalFilter[]
	) => void;
	fiscalYearStartMonth: Nullable<number>;
};

const ReportTypeViewBulkFilterModal: React.FC<Props> = ({
	closeModal,
	showModal,
	lazyLoadReportTypes,
	reportTypeOptions,
	currentEndDate,
	currentPeriod,
	currentStartDate,
	currentSearch,
	lazyLoadAccountOptions,
	accountOptions,
	equipmentOptions,
	lazyLoadEquipmentOptions,
	currentAdditionalFilters,
	bulkEditTableFilters,
	jobOptions,
	lazyLoadJobOptions,
	loadReportBlockFieldsForFilter,
	currentReportTypeFilter,
	fiscalYearStartMonth,
}) => {
	const [bulkFilterReportType, setBulkFilterReportType] = React.useState<Nullable<ReportTypeFilterItem>>(currentReportTypeFilter);
	const [bulkStartDate, setBulkStartDate] = React.useState(currentStartDate);
	const [bulkEndDate, setBulkEndDate] = React.useState(currentEndDate);
	const [bulkPeriod, setBulkPeriod] = React.useState(currentPeriod);
	const [bulkSearch, setBulkSearch] = React.useState(currentSearch);

	const loadReportBlockFieldOptions = React.useCallback(async () => {
		if (!bulkFilterReportType) {
			return [];
		}

		const newOptions = await loadReportBlockFieldsForFilter(
			bulkFilterReportType.id,
			formatDate(bulkStartDate, TimeFormat.DB_DATE_ONLY, TimeFormat.ISO_DATETIME),
			formatDate(bulkEndDate, TimeFormat.DB_DATE_ONLY, TimeFormat.ISO_DATETIME)
		);
		return newOptions;
	}, [bulkEndDate, bulkFilterReportType, bulkStartDate, loadReportBlockFieldsForFilter]);

	const { options: reportBlockFieldOptions, lazyLoad: lazyLoadReportBlockFields } = useLazyLoadWithInvalidation(
		loadReportBlockFieldOptions,
		[bulkEndDate, bulkFilterReportType, bulkStartDate, bulkPeriod]
	);

	const {
		resetAdditionalFilters,
		editFilter,
		additionalFilters: bulkAdditionalFilters,
		addAdditionalFilter,
		removeFilter,
	} = useReportTypeViewAdditionalFields(currentAdditionalFilters);

	const onBulkSearchChange = React.useCallback((value: string) => {
		setBulkSearch(value);
	}, []);

	const onBulkFilterReportTypeChange = React.useCallback((_newReportType: Nullable<ReportTypeFilterItem>) => {
		setBulkFilterReportType(_newReportType);
		resetAdditionalFilters();
	}, [resetAdditionalFilters]);

	const onApplyClick = React.useCallback(() => {
		if (!bulkFilterReportType) {
			return;
		}
		const _newAdditionalFilters = bulkAdditionalFilters.reduce<ReportTypeViewTableAdditionalFilter[]>((_acc, _filter) => {
			if (_filter.value === null) {
				return _acc;
			}
			_acc.push({
				..._filter,
				id: nanoid(UNIQUE_ID_SIZE),
			});
			return _acc;
		}, []);
		bulkEditTableFilters(bulkFilterReportType, bulkStartDate, bulkEndDate, bulkPeriod, bulkSearch, _newAdditionalFilters);
		closeModal();
	}, [bulkAdditionalFilters, bulkEditTableFilters, bulkEndDate, bulkFilterReportType, bulkPeriod, bulkSearch, bulkStartDate, closeModal]);

	return (
		<CustomModal
			closeModal={closeModal}
			modalStyle="info"
			showModal={showModal}
			size="lg"
		>
			<CustomModal.Header
				closeModal={closeModal}
				title="Advanced Search Options"
			/>
			<CustomModal.Body>
				<div className={styles['advanced-search-modal']}>
					<div className={styles['advanced-search-modal__main-filters']}>
						<span>
							Report Type
						</span>
						<div className={styles['advanced-search-modal__main-filters--align-right']}>
							<ReportTypeFilter
								defaultValue={bulkFilterReportType}
								lazyLoadReportTypes={lazyLoadReportTypes}
								reportTypeOptions={reportTypeOptions}
								setFilterByReportType={onBulkFilterReportTypeChange}
							/>
						</div>
						<span>
							Date
						</span>
						<div className={styles['advanced-search-modal__main-filters--align-right']}>
							<ReportTypeViewTableDateFilter
								endDate={bulkEndDate}
								fiscalYearStartMonth={fiscalYearStartMonth}
								period={bulkPeriod}
								setEndDate={setBulkEndDate}
								setPeriod={setBulkPeriod}
								setStartDate={setBulkStartDate}
								startDate={bulkStartDate}
							/>
						</div>
						<span>
							Search Query
						</span>

						<div className={styles['advanced-search-modal__main-filters--align-right']}>
							<SearchInput
								name="searchQuery"
								onChange={onBulkSearchChange}
								placeholder="Search"
								value={bulkSearch}
							/>
						</div>
					</div>
					{bulkFilterReportType && (
						<div className={styles['advanced-search-modal__add-button']}>
							<AddFilter
								addAdditionalFilter={addAdditionalFilter}
								options={ADDITIONAL_OPTIONS}
							/>
						</div>
					)}
					{!!bulkAdditionalFilters.length && (
						<div className={styles['advanced-search-modal__additional-filters']}>
							<AdditionalFilters
								accountOptions={accountOptions}
								editFilter={editFilter}
								equipmentOptions={equipmentOptions}
								filters={bulkAdditionalFilters}
								jobOptions={jobOptions}
								lazyLoadAccountOptions={lazyLoadAccountOptions}
								lazyLoadEquipmentOptions={lazyLoadEquipmentOptions}
								lazyLoadJobOptions={lazyLoadJobOptions}
								lazyLoadReportBlockFieldsOptions={lazyLoadReportBlockFields}
								removeFilter={removeFilter}
								reportBlockFieldOptions={reportBlockFieldOptions}
								resetAdditionalFilters={resetAdditionalFilters}
							/>
						</div>
					)}
				</div>
			</CustomModal.Body>
			<CustomModal.Footer>
				<Button
					label="Cancel"
					onClick={closeModal}
					style="secondary"
				/>
				<Button
					label="Apply Filters"
					onClick={onApplyClick}
					style="primary"
				/>
			</CustomModal.Footer>
		</CustomModal>
	);
};

export default React.memo(ReportTypeViewBulkFilterModal);

